import React from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { Sparkle, ArrowsClockwise, FileMagnifyingGlass } from "@phosphor-icons/react";
import {
  AnalysisEmptyDescription,
  AnalysisEmptyIconRing,
  AnalysisEmptyTitle,
  AnalysisEmptyWrapper,
} from "./AIPaneStyled";
import { brand, spacing, text, typography } from "../../theme/tokens";

/**
 * Readiness gate for the viewer's document-scoped chat.
 *
 * Rendered by `ChatPane` (in place of the conversation area) whenever the
 * chat scope is "This document" and the active document is not yet
 * chat-ready. Three faces:
 *
 *   not_ready → "Prepare for AI chat" CTA (the whole reason this exists:
 *               legacy PDFs become conversable on demand)
 *   in-flight → progress label per pipeline stage (materializing /
 *               queued / extracting / embedding)
 *   failed    → error detail + Retry
 *
 * Kept presentation-only: readiness data + the prepare callback come from
 * `useDocumentAiReadiness` via ChatPane so this stays trivially testable.
 */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(ArrowsClockwise)`
  animation: ${spin} 1.6s linear infinite;
`;

const GateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  border: none;
  border-radius: 8px;
  background: ${brand.primary};
  color: #ffffff;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GateErrorText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: ${typography.size.sm};
  color: #b42318;
`;

const GateHintText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: ${typography.size.sm};
  color: ${text.secondary};
`;

const IN_FLIGHT_COPY = {
  materializing: "Preparing the file…",
  queued: "Waiting in the processing queue…",
  extracting: "Extracting text…",
  embedding: "Indexing for search…",
};

const DocumentChatGate = ({
  readiness,
  loading,
  preparing,
  prepareError,
  onPrepare,
  onRetryStatus,
}) => {
  const state = readiness?.state || "not_ready";
  const prepareEnabled = readiness?.prepare_enabled !== false;

  // Readiness fetch still in flight: block the surface (the parent
  // disables the composer too) rather than letting document-scoped
  // chat run without its scope resolved (Bugbot on PR #234).
  if (loading) {
    return (
      <AnalysisEmptyWrapper data-testid="document-chat-gate-loading">
        <AnalysisEmptyIconRing>
          <SpinningIcon size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Checking AI readiness…</AnalysisEmptyTitle>
      </AnalysisEmptyWrapper>
    );
  }

  // Readiness fetch failed (readiness never arrived): don't silently
  // fall through to an ungated chat — offer a status retry instead.
  if (!readiness) {
    return (
      <AnalysisEmptyWrapper data-testid="document-chat-gate-unavailable">
        <AnalysisEmptyIconRing>
          <FileMagnifyingGlass size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Status unavailable</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Couldn&apos;t check whether this document is ready for AI chat.
        </AnalysisEmptyDescription>
        {onRetryStatus && (
          <GateButton onClick={onRetryStatus}>
            <ArrowsClockwise size={16} />
            Check again
          </GateButton>
        )}
      </AnalysisEmptyWrapper>
    );
  }

  if (IN_FLIGHT_COPY[state]) {
    return (
      <AnalysisEmptyWrapper data-testid="document-chat-gate-progress">
        <AnalysisEmptyIconRing>
          <SpinningIcon size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>{IN_FLIGHT_COPY[state]}</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Getting this document ready for AI chat. Large files (thousands of
          pages) can take a while — you can keep working and come back.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  if (state === "failed") {
    return (
      <AnalysisEmptyWrapper data-testid="document-chat-gate-failed">
        <AnalysisEmptyIconRing>
          <FileMagnifyingGlass size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Preparation failed</AnalysisEmptyTitle>
        {readiness?.detail ? (
          <GateErrorText>{readiness.detail}</GateErrorText>
        ) : (
          <AnalysisEmptyDescription>
            Something went wrong while preparing this document.
          </AnalysisEmptyDescription>
        )}
        <GateButton onClick={onPrepare} disabled={preparing || !prepareEnabled}>
          <ArrowsClockwise size={16} />
          Try again
        </GateButton>
        {prepareError && <GateErrorText>{prepareError}</GateErrorText>}
      </AnalysisEmptyWrapper>
    );
  }

  return (
    <AnalysisEmptyWrapper data-testid="document-chat-gate-cta">
      <AnalysisEmptyIconRing>
        <Sparkle size={32} weight="fill" />
      </AnalysisEmptyIconRing>
      <AnalysisEmptyTitle>Chat with this document</AnalysisEmptyTitle>
      <AnalysisEmptyDescription>
        Prepare this PDF for AI questioning — extraction and indexing let you
        ask things like &ldquo;list all of his conditions&rdquo; or
        &ldquo;show me every mental health entry.&rdquo;
      </AnalysisEmptyDescription>
      <GateButton
        onClick={onPrepare}
        disabled={preparing || !prepareEnabled}
        data-testid="document-chat-prepare-button"
      >
        <Sparkle size={16} weight="fill" />
        {preparing ? "Starting…" : "Prepare for AI chat"}
      </GateButton>
      {!prepareEnabled && (
        <GateHintText>
          AI document preparation is not enabled on this environment yet.
        </GateHintText>
      )}
      {prepareError && <GateErrorText>{prepareError}</GateErrorText>}
    </AnalysisEmptyWrapper>
  );
};

DocumentChatGate.propTypes = {
  readiness: PropTypes.shape({
    state: PropTypes.string,
    detail: PropTypes.string,
    prepare_enabled: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  preparing: PropTypes.bool,
  prepareError: PropTypes.string,
  onPrepare: PropTypes.func.isRequired,
  onRetryStatus: PropTypes.func,
};

DocumentChatGate.defaultProps = {
  readiness: null,
  loading: false,
  preparing: false,
  prepareError: null,
  onRetryStatus: undefined,
};

/**
 * Scope toggle shown above the chat input: "This document" (doc-scoped
 * retrieval), a saved collection, or "Whole matter" (the pre-existing
 * behavior).
 *
 * The collection option is a <select> rather than a third pill because a
 * matter can hold many collections; the pills stay for the two scopes
 * that are always singular.
 */
const ScopeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[4]};
  font-size: ${typography.size.sm};
  color: ${text.secondary};
`;

const ScopeButton = styled.button`
  border: 1px solid ${(props) => (props.$active ? brand.primary : "#d0d5dd")};
  background: ${(props) => (props.$active ? brand.primaryLight : "#ffffff")};
  color: ${(props) => (props.$active ? brand.primary : text.secondary)};
  border-radius: 999px;
  padding: 2px 10px;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  cursor: pointer;
`;

const ScopeSelect = styled.select`
  border: 1px solid ${(props) => (props.$active ? brand.primary : "#d0d5dd")};
  background: ${(props) => (props.$active ? brand.primaryLight : "#ffffff")};
  color: ${(props) => (props.$active ? brand.primary : text.secondary)};
  border-radius: 999px;
  padding: 2px 8px;
  max-width: 180px;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  cursor: pointer;
`;

export const ChatScopeToggle = ({
  scope,
  onChange,
  documentId,
  collections,
  collectionId,
  onCollectionChange,
}) => (
  <ScopeRow data-testid="chat-scope-toggle">
    <span>Asking about:</span>
    {Boolean(documentId) && (
      <ScopeButton
        type="button"
        $active={scope === "document"}
        onClick={() => onChange("document")}
      >
        This document
      </ScopeButton>
    )}
    {collections?.length > 0 && (
      <ScopeSelect
        data-testid="chat-scope-collection-select"
        aria-label="Ask about a document collection"
        $active={scope === "collection"}
        value={scope === "collection" ? String(collectionId || "") : ""}
        onChange={(event) => {
          const next = event.target.value;
          // Choosing a collection is itself the scope switch; making the
          // user pick a pill and then a collection would let the two
          // disagree.
          if (!next) {
            onChange("matter");
            return;
          }
          onCollectionChange(next);
          onChange("collection");
        }}
      >
        <option value="">A collection…</option>
        {collections.map((item) => (
          <option key={item.id} value={String(item.id)}>
            {item.name}
          </option>
        ))}
      </ScopeSelect>
    )}
    <ScopeButton
      type="button"
      $active={scope === "matter"}
      onClick={() => onChange("matter")}
    >
      Whole matter
    </ScopeButton>
  </ScopeRow>
);

ChatScopeToggle.propTypes = {
  scope: PropTypes.oneOf(["document", "collection", "matter"]).isRequired,
  onChange: PropTypes.func.isRequired,
  documentId: PropTypes.string,
  collections: PropTypes.array,
  collectionId: PropTypes.string,
  onCollectionChange: PropTypes.func,
};

ChatScopeToggle.defaultProps = {
  documentId: null,
  collections: [],
  collectionId: null,
  onCollectionChange: () => {},
};

export default DocumentChatGate;
